/** This file must only contain pure code and pure imports */

import { FlowGraphBlock, type IFlowGraphBlockConfiguration } from "../../../flowGraphBlock";
import { FlowGraphTypes, getRichTypeByFlowGraphType, RichTypeAny, RichTypeBoolean, RichTypeFlowGraphInteger, RichTypeNumber } from "../../../flowGraphRichTypes.pure";
import { FlowGraphBinaryOperationBlock } from "../flowGraphBinaryOperationBlock";
import { FlowGraphConstantOperationBlock } from "../flowGraphConstantOperationBlock";
import { Quaternion, Matrix, Vector2, Vector3, Vector4 } from "../../../../Maths/math.vector.pure";
import { FlowGraphUnaryOperationBlock } from "../flowGraphUnaryOperationBlock";
import { FlowGraphTernaryOperationBlock } from "../flowGraphTernaryOperationBlock";
import { FlowGraphInteger } from "../../../CustomTypes/flowGraphInteger.pure";
import { FlowGraphBlockNames } from "../../flowGraphBlockNames";
import { type FlowGraphDataConnection } from "core/FlowGraph/flowGraphDataConnection.pure";
import { type FlowGraphContext } from "core/FlowGraph/flowGraphContext";
import { FlowGraphMatrix2D, FlowGraphMatrix3D } from "core/FlowGraph/CustomTypes/flowGraphMatrix";
import {
    type FlowGraphMathOperationType,
    type FlowGraphNumber,
    _AreSameIntegerClass,
    _AreSameMatrixClass,
    _AreSameVectorOrQuaternionClass,
    _GetClassNameOf,
    getNumericValue,
    isNumeric,
} from "core/FlowGraph/utils";
import { RegisterClass } from "../../../../Misc/typeStore";

/**
 * A configuration interface for math blocks
 */
export interface IFlowGraphMathBlockConfiguration extends IFlowGraphBlockConfiguration {
    /**
     * If true, the multiplication is done per component.
     * This is the behavior in glTF interactivity.
     */
    useMatrixPerComponent?: boolean;

    /**
     * The type of the variable.
     */
    type?: FlowGraphTypes;

    /**
     * If true, the block will not allow integer and float arithmetic.
     * This is the behavior in glTF interactivity.
     */
    preventIntegerFloatArithmetic?: boolean;
}

/**
 * Polymorphic add block.
 */
export class FlowGraphAddBlock extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, FlowGraphMathOperationType> {
    /**
     * Construct a new add block.
     * @param config optional configuration
     */
    constructor(config?: IFlowGraphMathBlockConfiguration) {
        super(
            getRichTypeByFlowGraphType(config?.type),
            getRichTypeByFlowGraphType(config?.type),
            getRichTypeByFlowGraphType(config?.type),
            (a, b) => this._polymorphicAdd(a, b),
            FlowGraphBlockNames.Add,
            config
        );
    }

    private _polymorphicAdd(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType) {
        const aClassName = _GetClassNameOf(a);
        const bClassName = _GetClassNameOf(b);
        if (_AreSameVectorOrQuaternionClass(aClassName, bClassName) || _AreSameMatrixClass(aClassName, bClassName) || _AreSameIntegerClass(aClassName, bClassName)) {
            // cast to vector3, but any other cast will be fine
            return (a as Vector3).add(b as Vector3);
        } else if (aClassName === FlowGraphTypes.Quaternion || bClassName === FlowGraphTypes.Vector4) {
            return new Vector4((a as Quaternion).x, (a as Quaternion).y, (a as Quaternion).z, (a as Quaternion).w).addInPlace(b as Vector4);
        } else if (aClassName === FlowGraphTypes.Vector4 || bClassName === FlowGraphTypes.Quaternion) {
            return (a as Vector4).add(b as Quaternion);
        } else {
            // at this point at least one of the variables is a number.
            if (this.config?.preventIntegerFloatArithmetic && typeof a !== typeof b) {
                throw new Error("Cannot add different types of numbers.");
            }
            return getNumericValue(a as number) + getNumericValue(b as number);
        }
    }
}

/**
 * Polymorphic subtract block.
 */
export class FlowGraphSubtractBlock extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, FlowGraphMathOperationType> {
    /**
     * Construct a new subtract block.
     * @param config optional configuration
     */
    constructor(config?: IFlowGraphMathBlockConfiguration) {
        super(
            getRichTypeByFlowGraphType(config?.type),
            getRichTypeByFlowGraphType(config?.type),
            getRichTypeByFlowGraphType(config?.type),
            (a, b) => this._polymorphicSubtract(a, b),
            FlowGraphBlockNames.Subtract,
            config
        );
    }

    private _polymorphicSubtract(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType) {
        const aClassName = _GetClassNameOf(a);
        const bClassName = _GetClassNameOf(b);
        if (_AreSameVectorOrQuaternionClass(aClassName, bClassName) || _AreSameIntegerClass(aClassName, bClassName) || _AreSameMatrixClass(aClassName, bClassName)) {
            // cast to vector3, but it can be casted to any vector type
            return (a as Vector3).subtract(b as Vector3);
        } else if (aClassName === FlowGraphTypes.Quaternion || bClassName === FlowGraphTypes.Vector4) {
            return new Vector4((a as Quaternion).x, (a as Quaternion).y, (a as Quaternion).z, (a as Quaternion).w).subtractInPlace(b as Vector4);
        } else if (aClassName === FlowGraphTypes.Vector4 || bClassName === FlowGraphTypes.Quaternion) {
            return (a as Vector4).subtract(b as Quaternion);
        } else {
            // at this point at least one of the variables is a number.
            if (this.config?.preventIntegerFloatArithmetic && typeof a !== typeof b) {
                throw new Error("Cannot add different types of numbers.");
            }
            return getNumericValue(a as number) - getNumericValue(b as number);
        }
    }
}

/**
 * Polymorphic multiply block.
 * In case of matrix, it is configurable whether the multiplication is done per component.
 */
export class FlowGraphMultiplyBlock extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphMathBlockConfiguration) {
        super(
            getRichTypeByFlowGraphType(config?.type),
            getRichTypeByFlowGraphType(config?.type),
            getRichTypeByFlowGraphType(config?.type),
            (a, b) => this._polymorphicMultiply(a, b),
            FlowGraphBlockNames.Multiply,
            config
        );
    }

    private _polymorphicMultiply(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType) {
        const aClassName = _GetClassNameOf(a);
        const bClassName = _GetClassNameOf(b);
        if (_AreSameVectorOrQuaternionClass(aClassName, bClassName) || _AreSameIntegerClass(aClassName, bClassName)) {
            // cast to vector3, but it can be casted to any vector type
            return (a as Vector3).multiply(b as Vector3);
        } else if (aClassName === FlowGraphTypes.Quaternion || bClassName === FlowGraphTypes.Vector4) {
            return new Vector4((a as Quaternion).x, (a as Quaternion).y, (a as Quaternion).z, (a as Quaternion).w).multiplyInPlace(b as Vector4);
        } else if (aClassName === FlowGraphTypes.Vector4 || bClassName === FlowGraphTypes.Quaternion) {
            return (a as Vector4).multiply(b as Quaternion);
        } else if (_AreSameMatrixClass(aClassName, bClassName)) {
            if (this.config?.useMatrixPerComponent) {
                // this is the definition of multiplication of glTF interactivity
                // get a's m as array, and multiply each component with b's m
                const aM = (a as FlowGraphMatrix2D).m;
                for (let i = 0; i < aM.length; i++) {
                    aM[i] *= (b as FlowGraphMatrix2D).m[i];
                }
                if (aClassName === FlowGraphTypes.Matrix2D) {
                    return new FlowGraphMatrix2D(aM);
                } else if (aClassName === FlowGraphTypes.Matrix3D) {
                    return new FlowGraphMatrix3D(aM);
                } else {
                    return Matrix.FromArray(aM);
                }
            } else {
                a = a as Matrix;
                b = b as Matrix;
                return b.multiply(a);
            }
        } else {
            // at this point at least one of the variables is a number.
            if (this.config?.preventIntegerFloatArithmetic && typeof a !== typeof b) {
                throw new Error("Cannot add different types of numbers.");
            }
            return getNumericValue(a as number) * getNumericValue(b as number);
        }
    }
}

/**
 * Polymorphic division block.
 */
export class FlowGraphDivideBlock extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, FlowGraphMathOperationType> {
    /**
     * Construct a new divide block.
     * @param config - Optional configuration
     */
    constructor(config?: IFlowGraphMathBlockConfiguration) {
        super(
            getRichTypeByFlowGraphType(config?.type),
            getRichTypeByFlowGraphType(config?.type),
            getRichTypeByFlowGraphType(config?.type),
            (a, b) => this._polymorphicDivide(a, b),
            FlowGraphBlockNames.Divide,
            config
        );
    }

    private _polymorphicDivide(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType) {
        const aClassName = _GetClassNameOf(a);
        const bClassName = _GetClassNameOf(b);
        if (_AreSameVectorOrQuaternionClass(aClassName, bClassName) || _AreSameIntegerClass(aClassName, bClassName)) {
            // cast to vector3, but it can be casted to any vector type
            return (a as Vector3).divide(b as Vector3);
        } else if (aClassName === FlowGraphTypes.Quaternion || bClassName === FlowGraphTypes.Quaternion) {
            // this is a simple division (per component!), and should be also supported between Quat and Vector4. Therefore -
            const aClone = (a as Quaternion).clone();
            aClone.x /= (b as Quaternion).x;
            aClone.y /= (b as Quaternion).y;
            aClone.z /= (b as Quaternion).z;
            aClone.w /= (b as Quaternion).w;
            return aClone;
        } else if (aClassName === FlowGraphTypes.Quaternion || bClassName === FlowGraphTypes.Vector4) {
            return new Vector4((a as Quaternion).x, (a as Quaternion).y, (a as Quaternion).z, (a as Quaternion).w).divideInPlace(b as Vector4);
        } else if (aClassName === FlowGraphTypes.Vector4 || bClassName === FlowGraphTypes.Quaternion) {
            return (a as Vector4).divide(b as Quaternion);
        } else if (_AreSameMatrixClass(aClassName, bClassName)) {
            if (this.config?.useMatrixPerComponent) {
                // get a's m as array, and divide each component with b's m
                const aM = (a as FlowGraphMatrix2D).m;
                for (let i = 0; i < aM.length; i++) {
                    aM[i] /= (b as FlowGraphMatrix2D).m[i];
                }
                if (aClassName === FlowGraphTypes.Matrix2D) {
                    return new FlowGraphMatrix2D(aM);
                } else if (aClassName === FlowGraphTypes.Matrix3D) {
                    return new FlowGraphMatrix3D(aM);
                } else {
                    return Matrix.FromArray(aM);
                }
            } else {
                a = a as Matrix;
                b = b as Matrix;
                return a.divide(b);
            }
        } else {
            // at this point at least one of the variables is a number.
            if (this.config?.preventIntegerFloatArithmetic && typeof a !== typeof b) {
                throw new Error("Cannot add different types of numbers.");
            }
            return getNumericValue(a as number) / getNumericValue(b as number);
        }
    }
}

/**
 * Configuration interface for the random block.
 */
export interface IFlowGraphRandomBlockConfiguration extends IFlowGraphBlockConfiguration {
    /**
     * The minimum value. defaults to 0.
     */
    min?: number;
    /**
     * The maximum value. defaults to 1.
     */
    max?: number;

    /**
     * The seed for the random number generator for deterministic random values.
     * If not set, Math.random() is used.
     */
    seed?: number;
}

/**
 * Random number between min and max (defaults to 0 to 1)
 *
 * This node will cache the result for he same node reference. i.e., a Math.eq that references the SAME random node will always return true.
 */
export class FlowGraphRandomBlock extends FlowGraphConstantOperationBlock<FlowGraphMathOperationType> {
    /**
     * The minimum value. defaults to 0.
     */
    public readonly min: FlowGraphDataConnection<number>;
    /**
     * The maximum value. defaults to 1.
     */
    public readonly max: FlowGraphDataConnection<number>;

    private _seed?: number;

    /**
     * Construct a new random block.
     * @param config optional configuration
     */
    constructor(config?: IFlowGraphRandomBlockConfiguration) {
        super(RichTypeNumber, (context) => this._random(context), FlowGraphBlockNames.Random, config);
        this.min = this.registerDataInput("min", RichTypeNumber, config?.min ?? 0);
        this.max = this.registerDataInput("max", RichTypeNumber, config?.max ?? 1);
        if (config?.seed) {
            this._seed = config.seed;
        }
    }

    private _isSeed(seed = this._seed): seed is number {
        return seed !== undefined;
    }

    private _getRandomValue() {
        if (this._isSeed(this._seed)) {
            // compute seed-based random number, deterministic randomness!
            const x = Math.sin(this._seed++) * 10000;
            return x - Math.floor(x);
        }
        return Math.random();
    }

    private _random(context: FlowGraphContext) {
        const min = this.min.getValue(context);
        const max = this.max.getValue(context);
        return this._getRandomValue() * (max - min) + min;
    }
}

/**
 * E constant.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export class FlowGraphEBlock extends FlowGraphConstantOperationBlock<number> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, () => Math.E, FlowGraphBlockNames.E, config);
    }
}

/**
 * Pi constant.
 */
export class FlowGraphPiBlock extends FlowGraphConstantOperationBlock<number> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, () => Math.PI, FlowGraphBlockNames.PI, config);
    }
}

/**
 * Tau constant (2 * Pi), the ratio of a circle's circumference to its radius.
 */
export class FlowGraphTauBlock extends FlowGraphConstantOperationBlock<number> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, () => 2 * Math.PI, FlowGraphBlockNames.Tau, config);
    }
}

/**
 * Positive inf constant.
 */
export class FlowGraphInfBlock extends FlowGraphConstantOperationBlock<number> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, () => Number.POSITIVE_INFINITY, FlowGraphBlockNames.Inf, config);
    }
}

/**
 * NaN constant.
 */
export class FlowGraphNaNBlock extends FlowGraphConstantOperationBlock<number> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, () => Number.NaN, FlowGraphBlockNames.NaN, config);
    }
}

function ComponentWiseUnaryOperation(a: FlowGraphMathOperationType, op: (a: number) => number) {
    const aClassName = _GetClassNameOf(a);
    switch (aClassName) {
        case "FlowGraphInteger":
            a = a as FlowGraphInteger;
            return new FlowGraphInteger(op(a.value));
        case FlowGraphTypes.Vector2:
            a = a as Vector2;
            return new Vector2(op(a.x), op(a.y));
        case FlowGraphTypes.Vector3:
            a = a as Vector3;
            return new Vector3(op(a.x), op(a.y), op(a.z));
        case FlowGraphTypes.Vector4:
            a = a as Vector4;
            return new Vector4(op(a.x), op(a.y), op(a.z), op(a.w));
        case FlowGraphTypes.Quaternion:
            a = a as Quaternion;
            return new Quaternion(op(a.x), op(a.y), op(a.z), op(a.w));
        case FlowGraphTypes.Matrix:
            a = a as Matrix;
            return Matrix.FromArray(a.m.map(op));
        case FlowGraphTypes.Matrix2D:
            a = a as FlowGraphMatrix2D;
            // reason for not using .map is performance
            return new FlowGraphMatrix2D(a.m.map(op));
        case FlowGraphTypes.Matrix3D:
            a = a as FlowGraphMatrix3D;
            return new FlowGraphMatrix3D(a.m.map(op));
        default:
            a = a as number;
            return op(a);
    }
}

/**
 * Absolute value block.
 */
export class FlowGraphAbsBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicAbs(a), FlowGraphBlockNames.Abs, config);
    }

    private _polymorphicAbs(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.abs);
    }
}

/**
 * Sign block.
 */
export class FlowGraphSignBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicSign(a), FlowGraphBlockNames.Sign, config);
    }

    private _polymorphicSign(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.sign);
    }
}

/**
 * Truncation block.
 */
export class FlowGraphTruncBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicTrunc(a), FlowGraphBlockNames.Trunc, config);
    }

    private _polymorphicTrunc(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.trunc);
    }
}

/**
 * Floor block.
 */
export class FlowGraphFloorBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicFloor(a), FlowGraphBlockNames.Floor, config);
    }

    private _polymorphicFloor(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.floor);
    }
}

/**
 * Ceiling block.
 */
export class FlowGraphCeilBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicCeiling(a), FlowGraphBlockNames.Ceil, config);
    }

    private _polymorphicCeiling(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.ceil);
    }
}

/**
 * Configuration for the round block.
 */
export interface IFlowGraphRoundBlockConfiguration extends IFlowGraphBlockConfiguration {
    /**
     * If true, the rounding is away from zero, even when negative. i.e. -7.5 goes to -8, and not -7 as Math.round does (it rounds up).
     * This is the default when using glTF
     */
    roundHalfAwayFromZero?: boolean;
}
/**
 * Round block.
 */
export class FlowGraphRoundBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphRoundBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicRound(a), FlowGraphBlockNames.Round, config);
    }

    private _polymorphicRound(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, (a) => (a < 0 && this.config?.roundHalfAwayFromZero ? -Math.round(-a) : Math.round(a)));
    }
}

/**
 * A block that returns the fractional part of a number.
 */
export class FlowGraphFractionBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicFraction(a), FlowGraphBlockNames.Fraction, config);
    }

    private _polymorphicFraction(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, (a) => a - Math.floor(a));
    }
}

/**
 * Negation block.
 */
export class FlowGraphNegationBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    /**
     * construct a new negation block.
     * @param config optional configuration
     */
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicNeg(a), FlowGraphBlockNames.Negation, config);
    }

    private _polymorphicNeg(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, (a) => -a);
    }
}

function ComponentWiseBinaryOperation(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType, op: (a: number, b: number) => number) {
    const aClassName = _GetClassNameOf(a);
    switch (aClassName) {
        case "FlowGraphInteger":
            a = a as FlowGraphInteger;
            b = b as FlowGraphInteger;
            return new FlowGraphInteger(op(a.value, b.value));
        case FlowGraphTypes.Vector2:
            a = a as Vector2;
            b = b as Vector2;
            return new Vector2(op(a.x, b.x), op(a.y, b.y));
        case FlowGraphTypes.Vector3:
            a = a as Vector3;
            b = b as Vector3;
            return new Vector3(op(a.x, b.x), op(a.y, b.y), op(a.z, b.z));
        case FlowGraphTypes.Vector4:
            a = a as Vector4;
            b = b as Vector4;
            return new Vector4(op(a.x, b.x), op(a.y, b.y), op(a.z, b.z), op(a.w, b.w));
        case FlowGraphTypes.Quaternion:
            a = a as Quaternion;
            b = b as Quaternion;
            return new Quaternion(op(a.x, b.x), op(a.y, b.y), op(a.z, b.z), op(a.w, b.w));
        case FlowGraphTypes.Matrix:
            a = a as Matrix;
            return Matrix.FromArray(a.m.map((v, i) => op(v, (b as Matrix).m[i])));
        case FlowGraphTypes.Matrix2D:
            a = a as FlowGraphMatrix2D;
            return new FlowGraphMatrix2D(a.m.map((v, i) => op(v, (b as FlowGraphMatrix2D).m[i])));
        case FlowGraphTypes.Matrix3D:
            a = a as FlowGraphMatrix3D;
            return new FlowGraphMatrix3D(a.m.map((v, i) => op(v, (b as FlowGraphMatrix3D).m[i])));
        default:
            return op(getNumericValue(a as number), getNumericValue(b as number));
    }
}

/**
 * Remainder block.
 */
export class FlowGraphModuloBlock extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeAny, (a, b) => this._polymorphicRemainder(a, b), FlowGraphBlockNames.Modulo, config);
    }

    private _polymorphicRemainder(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType) {
        return ComponentWiseBinaryOperation(a, b, (a, b) => a % b);
    }
}

/**
 * Min block.
 */
export class FlowGraphMinBlock extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeAny, (a, b) => this._polymorphicMin(a, b), FlowGraphBlockNames.Min, config);
    }

    private _polymorphicMin(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType) {
        return ComponentWiseBinaryOperation(a, b, Math.min);
    }
}

/**
 * Max block
 */
export class FlowGraphMaxBlock extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeAny, (a, b) => this._polymorphicMax(a, b), FlowGraphBlockNames.Max, config);
    }

    private _polymorphicMax(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType) {
        return ComponentWiseBinaryOperation(a, b, Math.max);
    }
}

function Clamp(a: number, b: number, c: number) {
    return Math.min(Math.max(a, Math.min(b, c)), Math.max(b, c));
}

function ComponentWiseTernaryOperation(
    a: FlowGraphMathOperationType,
    b: FlowGraphMathOperationType,
    c: FlowGraphMathOperationType,
    op: (a: number, b: number, c: number) => number
) {
    const aClassName = _GetClassNameOf(a);
    switch (aClassName) {
        case "FlowGraphInteger":
            a = a as FlowGraphInteger;
            b = b as FlowGraphInteger;
            c = c as FlowGraphInteger;
            return new FlowGraphInteger(op(a.value, b.value, c.value));
        case FlowGraphTypes.Vector2:
            a = a as Vector2;
            b = b as Vector2;
            c = c as Vector2;
            return new Vector2(op(a.x, b.x, c.x), op(a.y, b.y, c.y));
        case FlowGraphTypes.Vector3:
            a = a as Vector3;
            b = b as Vector3;
            c = c as Vector3;
            return new Vector3(op(a.x, b.x, c.x), op(a.y, b.y, c.y), op(a.z, b.z, c.z));
        case FlowGraphTypes.Vector4:
            a = a as Vector4;
            b = b as Vector4;
            c = c as Vector4;
            return new Vector4(op(a.x, b.x, c.x), op(a.y, b.y, c.y), op(a.z, b.z, c.z), op(a.w, b.w, c.w));
        case FlowGraphTypes.Quaternion:
            a = a as Quaternion;
            b = b as Quaternion;
            c = c as Quaternion;
            return new Quaternion(op(a.x, b.x, c.x), op(a.y, b.y, c.y), op(a.z, b.z, c.z), op(a.w, b.w, c.w));
        case FlowGraphTypes.Matrix:
            return Matrix.FromArray((a as Matrix).m.map((v, i) => op(v, (b as Matrix).m[i], (c as Matrix).m[i])));
        case FlowGraphTypes.Matrix2D:
            return new FlowGraphMatrix2D((a as FlowGraphMatrix2D).m.map((v, i) => op(v, (b as FlowGraphMatrix2D).m[i], (c as FlowGraphMatrix2D).m[i])));
        case FlowGraphTypes.Matrix3D:
            return new FlowGraphMatrix3D((a as FlowGraphMatrix3D).m.map((v, i) => op(v, (b as FlowGraphMatrix3D).m[i], (c as FlowGraphMatrix3D).m[i])));
        default:
            return op(getNumericValue(a as number), getNumericValue(b as number), getNumericValue(c as number));
    }
}

/**
 * Clamp block.
 */
export class FlowGraphClampBlock extends FlowGraphTernaryOperationBlock<
    FlowGraphMathOperationType,
    FlowGraphMathOperationType,
    FlowGraphMathOperationType,
    FlowGraphMathOperationType
> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeAny, RichTypeAny, (a, b, c) => this._polymorphicClamp(a, b, c), FlowGraphBlockNames.Clamp, config);
    }

    private _polymorphicClamp(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType, c: FlowGraphMathOperationType) {
        return ComponentWiseTernaryOperation(a, b, c, Clamp);
    }
}

function Saturate(a: number): number {
    return Math.min(Math.max(a, 0), 1);
}

/**
 * Saturate block.
 */
export class FlowGraphSaturateBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicSaturate(a), FlowGraphBlockNames.Saturate, config);
    }

    private _polymorphicSaturate(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Saturate);
    }
}

function Interpolate(a: number, b: number, c: number) {
    return (1 - c) * a + c * b;
}

/**
 * Smooth step (Hermite interpolation) coefficient.
 * Given the edges `a`/`b` and the value `c`, computes the smooth interpolation
 * coefficient `t * t * (3 - 2 * t)` where `t = saturate((c - min(a, b)) / |b - a|)`.
 * Note that this returns the coefficient in [0, 1]; it does not interpolate between `a` and `b`.
 * @param a first edge
 * @param b second edge
 * @param c value to interpolate
 * @returns the smooth-step interpolation coefficient
 */
function SmoothStep(a: number, b: number, c: number) {
    // Degenerate edges: with a === b the transition is instantaneous, so return a step rather than
    // dividing by zero (which would yield NaN and poison downstream math).
    if (a === b) {
        return c < a ? 0 : 1;
    }
    const t = Saturate((c - Math.min(a, b)) / Math.abs(b - a));
    return t * t * (3 - 2 * t);
}

/**
 * Interpolate block.
 */
export class FlowGraphMathInterpolationBlock extends FlowGraphTernaryOperationBlock<
    FlowGraphMathOperationType,
    FlowGraphMathOperationType,
    FlowGraphMathOperationType,
    FlowGraphMathOperationType
> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeAny, RichTypeAny, (a, b, c) => this._polymorphicInterpolate(a, b, c), FlowGraphBlockNames.MathInterpolation, config);
    }

    private _polymorphicInterpolate(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType, c: FlowGraphMathOperationType) {
        return ComponentWiseTernaryOperation(a, b, c, Interpolate);
    }
}

/**
 * Spherical linear interpolation between two unit quaternions, matching the
 * quaternion slerp operation. The two inputs are treated as
 * Babylon `Quaternion` values regardless of their concrete class (the spec
 * defines them as `float4`), and the output is a `Quaternion`.
 *
 * The underlying implementation is `Quaternion.Slerp`, whose algorithm matches
 * the spec step-for-step (dot product, conditional sign-flip on `b`, sin-based
 * weighting, and a near-identity short-circuit to a linear blend).
 */
export class FlowGraphMathSlerpBlock extends FlowGraphTernaryOperationBlock<Quaternion, Quaternion, number, Quaternion> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeNumber, RichTypeAny, (a, b, c) => Quaternion.Slerp(a, b, c), FlowGraphBlockNames.MathSlerp, config);
    }
}

/**
 * Smooth-step block, returning the Hermite interpolation coefficient.
 * Operates component-wise over floatN inputs (the two edges `a`/`b` and the
 * value `c`).
 */
export class FlowGraphMathSmoothStepBlock extends FlowGraphTernaryOperationBlock<
    FlowGraphMathOperationType,
    FlowGraphMathOperationType,
    FlowGraphMathOperationType,
    FlowGraphMathOperationType
> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeAny, RichTypeAny, (a, b, c) => this._polymorphicSmoothStep(a, b, c), FlowGraphBlockNames.SmoothStep, config);
    }

    private _polymorphicSmoothStep(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType, c: FlowGraphMathOperationType) {
        return ComponentWiseTernaryOperation(a, b, c, SmoothStep);
    }
}

/**
 * Equals block.
 */
export class FlowGraphEqualityBlock extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, boolean> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeBoolean, (a, b) => this._polymorphicEq(a, b), FlowGraphBlockNames.Equality, config);
    }

    private _polymorphicEq(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType) {
        const aClassName = _GetClassNameOf(a);
        const bClassName = _GetClassNameOf(b);
        // A rotation may arrive either as a Quaternion or as a four-component vector, so treat Quaternion and
        // Vector4 (both four-component) as comparable so that, for example, a decomposed rotation can be compared
        // against a float4 literal.
        const isFourComponent = (className?: string) => className === FlowGraphTypes.Vector4 || className === FlowGraphTypes.Quaternion;
        if (isFourComponent(aClassName) && isFourComponent(bClassName)) {
            return (a as Vector4).equals(b as Vector4);
        }
        if (_AreSameVectorOrQuaternionClass(aClassName, bClassName) || _AreSameMatrixClass(aClassName, bClassName) || _AreSameIntegerClass(aClassName, bClassName)) {
            return (a as Vector3).equals(b as Vector3);
        }
        // Handle mixed number/FlowGraphInteger comparison
        if (isNumeric(a) && isNumeric(b)) {
            return getNumericValue(a as FlowGraphNumber) === getNumericValue(b as FlowGraphNumber);
        }
        if (typeof a !== typeof b) {
            return false;
        }
        return a === b;
    }
}

function ComparisonOperators(a: FlowGraphNumber, b: FlowGraphNumber, op: (a: number, b: number) => boolean) {
    if (isNumeric(a) && isNumeric(b)) {
        return op(getNumericValue(a), getNumericValue(b));
    } else {
        throw new Error(`Cannot compare ${a} and ${b}`);
    }
}

/**
 * Less than block.
 */
export class FlowGraphLessThanBlock extends FlowGraphBinaryOperationBlock<FlowGraphNumber, FlowGraphNumber, boolean> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeBoolean, (a, b) => this._polymorphicLessThan(a, b), FlowGraphBlockNames.LessThan, config);
    }

    private _polymorphicLessThan(a: FlowGraphNumber, b: FlowGraphNumber) {
        return ComparisonOperators(a, b, (a, b) => a < b);
    }
}

/**
 * Less than or equal block.
 */
export class FlowGraphLessThanOrEqualBlock extends FlowGraphBinaryOperationBlock<FlowGraphNumber, FlowGraphNumber, boolean> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeBoolean, (a, b) => this._polymorphicLessThanOrEqual(a, b), FlowGraphBlockNames.LessThanOrEqual, config);
    }

    private _polymorphicLessThanOrEqual(a: FlowGraphNumber, b: FlowGraphNumber) {
        return ComparisonOperators(a, b, (a, b) => a <= b);
    }
}

/**
 * Greater than block.
 */
export class FlowGraphGreaterThanBlock extends FlowGraphBinaryOperationBlock<FlowGraphNumber, FlowGraphNumber, boolean> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeBoolean, (a, b) => this._polymorphicGreaterThan(a, b), FlowGraphBlockNames.GreaterThan, config);
    }

    private _polymorphicGreaterThan(a: FlowGraphNumber, b: FlowGraphNumber) {
        return ComparisonOperators(a, b, (a, b) => a > b);
    }
}

/**
 * Greater than or equal block.
 */
export class FlowGraphGreaterThanOrEqualBlock extends FlowGraphBinaryOperationBlock<FlowGraphNumber, FlowGraphNumber, boolean> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeBoolean, (a, b) => this._polymorphicGreaterThanOrEqual(a, b), FlowGraphBlockNames.GreaterThanOrEqual, config);
    }

    private _polymorphicGreaterThanOrEqual(a: FlowGraphNumber, b: FlowGraphNumber) {
        return ComparisonOperators(a, b, (a, b) => a >= b);
    }
}

/**
 * Is NaN block.
 */
export class FlowGraphIsNanBlock extends FlowGraphUnaryOperationBlock<FlowGraphNumber, boolean> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeBoolean, (a) => this._polymorphicIsNan(a), FlowGraphBlockNames.IsNaN, config);
    }

    private _polymorphicIsNan(a: FlowGraphNumber) {
        if (isNumeric(a, true)) {
            return isNaN(getNumericValue(a));
        } else {
            throw new Error(`Cannot get NaN of ${a}`);
        }
    }
}

/**
 * Is Inf block.
 */
export class FlowGraphIsInfinityBlock extends FlowGraphUnaryOperationBlock<FlowGraphNumber, boolean> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeBoolean, (a) => this._polymorphicIsInf(a), FlowGraphBlockNames.IsInfinity, config);
    }

    private _polymorphicIsInf(a: FlowGraphNumber) {
        if (isNumeric(a)) {
            return !isFinite(getNumericValue(a));
        } else {
            throw new Error(`Cannot get isInf of ${a}`);
        }
    }
}

/**
 * Convert degrees to radians block.
 */
export class FlowGraphDegToRadBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    /**
     * Constructs a new instance of the flow graph math block.
     * @param config - Optional configuration for the flow graph block.
     */
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicDegToRad(a), FlowGraphBlockNames.DegToRad, config);
    }

    private _degToRad(a: number) {
        return (a * Math.PI) / 180;
    }

    private _polymorphicDegToRad(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, this._degToRad);
    }
}

/**
 * Convert radians to degrees block.
 */
export class FlowGraphRadToDegBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicRadToDeg(a), FlowGraphBlockNames.RadToDeg, config);
    }

    private _radToDeg(a: number) {
        return (a * 180) / Math.PI;
    }

    private _polymorphicRadToDeg(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, this._radToDeg);
    }
}

/**
 * Sin block.
 */
export class FlowGraphSinBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicSin(a), FlowGraphBlockNames.Sin, config);
    }

    private _polymorphicSin(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.sin);
    }
}

/**
 * Cos block.
 */
export class FlowGraphCosBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicCos(a), FlowGraphBlockNames.Cos, config);
    }

    private _polymorphicCos(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.cos);
    }
}

/**
 * Tan block.
 */
export class FlowGraphTanBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicTan(a), FlowGraphBlockNames.Tan, config);
    }

    private _polymorphicTan(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.tan);
    }
}

/**
 * Arcsin block.
 */
export class FlowGraphAsinBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicAsin(a), FlowGraphBlockNames.Asin, config);
    }

    private _polymorphicAsin(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.asin);
    }
}

/**
 * Arccos block.
 */
export class FlowGraphAcosBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicAcos(a), FlowGraphBlockNames.Acos, config);
    }

    private _polymorphicAcos(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.acos);
    }
}

/**
 * Arctan block.
 */
export class FlowGraphAtanBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeNumber, RichTypeNumber, (a) => this._polymorphicAtan(a), FlowGraphBlockNames.Atan, config);
    }

    private _polymorphicAtan(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.atan);
    }
}

/**
 * Arctan2 block.
 */
export class FlowGraphAtan2Block extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, RichTypeAny, (a, b) => this._polymorphicAtan2(a, b), FlowGraphBlockNames.Atan2, config);
    }

    private _polymorphicAtan2(a: any, b: any) {
        return ComponentWiseBinaryOperation(a, b, Math.atan2);
    }
}

/**
 * Hyperbolic sin block.
 */
export class FlowGraphSinhBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicSinh(a), FlowGraphBlockNames.Sinh, config);
    }

    private _polymorphicSinh(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.sinh);
    }
}

/**
 * Hyperbolic cos block.
 */
export class FlowGraphCoshBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicCosh(a), FlowGraphBlockNames.Cosh, config);
    }

    private _polymorphicCosh(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.cosh);
    }
}

/**
 * Hyperbolic tan block.
 */
export class FlowGraphTanhBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeAny, (a) => this._polymorphicTanh(a), FlowGraphBlockNames.Tanh, config);
    }

    private _polymorphicTanh(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.tanh);
    }
}

/**
 * Hyperbolic arcsin block.
 */
export class FlowGraphAsinhBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, (a) => this._polymorphicAsinh(a), FlowGraphBlockNames.Asinh, config);
    }

    private _polymorphicAsinh(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.asinh);
    }
}

/**
 * Hyperbolic arccos block.
 */
export class FlowGraphAcoshBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, (a) => this._polymorphicAcosh(a), FlowGraphBlockNames.Acosh, config);
    }

    private _polymorphicAcosh(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.acosh);
    }
}

/**
 * Hyperbolic arctan block.
 */
export class FlowGraphAtanhBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, (a) => this._polymorphicAtanh(a), FlowGraphBlockNames.Atanh, config);
    }

    private _polymorphicAtanh(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.atanh);
    }
}

/**
 * Exponential block.
 */
export class FlowGraphExpBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, (a) => this._polymorphicExp(a), FlowGraphBlockNames.Exponential, config);
    }

    private _polymorphicExp(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.exp);
    }
}

/**
 * Logarithm block.
 */
export class FlowGraphLogBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, (a) => this._polymorphicLog(a), FlowGraphBlockNames.Log, config);
    }

    private _polymorphicLog(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.log);
    }
}

/**
 * Base 2 logarithm block.
 */
export class FlowGraphLog2Block extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, (a) => this._polymorphicLog2(a), FlowGraphBlockNames.Log2, config);
    }

    private _polymorphicLog2(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.log2);
    }
}

/**
 * Base 10 logarithm block.
 */
export class FlowGraphLog10Block extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, (a) => this._polymorphicLog10(a), FlowGraphBlockNames.Log10, config);
    }

    private _polymorphicLog10(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.log10);
    }
}

/**
 * Square root block.
 */
export class FlowGraphSquareRootBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, (a) => this._polymorphicSqrt(a), FlowGraphBlockNames.SquareRoot, config);
    }

    private _polymorphicSqrt(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.sqrt);
    }
}

/**
 * Cube root block.
 */
export class FlowGraphCubeRootBlock extends FlowGraphUnaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, (a) => this._polymorphicCubeRoot(a), FlowGraphBlockNames.CubeRoot, config);
    }

    private _polymorphicCubeRoot(a: FlowGraphMathOperationType) {
        return ComponentWiseUnaryOperation(a, Math.cbrt);
    }
}

/**
 * Power block.
 */
export class FlowGraphPowerBlock extends FlowGraphBinaryOperationBlock<FlowGraphMathOperationType, FlowGraphMathOperationType, FlowGraphMathOperationType> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeAny, RichTypeNumber, RichTypeNumber, (a, b) => this._polymorphicPow(a, b), FlowGraphBlockNames.Power, config);
    }

    private _polymorphicPow(a: FlowGraphMathOperationType, b: FlowGraphMathOperationType) {
        return ComponentWiseBinaryOperation(a, b, Math.pow);
    }
}

/**
 * Configuration for bitwise operators
 */
export interface IFlowGraphBitwiseBlockConfiguration extends IFlowGraphBlockConfiguration {
    /**
     * The type of the values that will be operated on
     * Defaults to FlowGraphInteger, but can be a number or boolean as well.
     */
    valueType: FlowGraphTypes;
}

type FlowGraphBitwiseTypes = FlowGraphInteger | FlowGraphNumber | boolean;
/**
 * Bitwise NOT operation
 */
export class FlowGraphBitwiseNotBlock extends FlowGraphUnaryOperationBlock<FlowGraphBitwiseTypes, FlowGraphBitwiseTypes> {
    constructor(config?: IFlowGraphBitwiseBlockConfiguration) {
        super(
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            (a) => {
                if (typeof a === "boolean") {
                    return !a;
                } else if (typeof a === "number") {
                    return ~a;
                }
                return new FlowGraphInteger(~a.value);
            },
            FlowGraphBlockNames.BitwiseNot,
            config
        );
    }
}

/**
 * Bitwise AND operation
 */
export class FlowGraphBitwiseAndBlock extends FlowGraphBinaryOperationBlock<FlowGraphBitwiseTypes, FlowGraphBitwiseTypes, FlowGraphBitwiseTypes> {
    constructor(config?: IFlowGraphBitwiseBlockConfiguration) {
        super(
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            (a, b) => {
                if (typeof a === "boolean" && typeof b === "boolean") {
                    return a && b;
                } else if (typeof a === "number" && typeof b === "number") {
                    return a & b;
                } else if (typeof a === "object" && typeof b === "object") {
                    return new FlowGraphInteger(a.value & b.value);
                } else {
                    throw new Error(`Cannot perform bitwise AND on ${a} and ${b}`);
                }
            },
            FlowGraphBlockNames.BitwiseAnd,
            config
        );
    }
}

/**
 * Bitwise OR operation
 */
export class FlowGraphBitwiseOrBlock extends FlowGraphBinaryOperationBlock<FlowGraphBitwiseTypes, FlowGraphBitwiseTypes, FlowGraphBitwiseTypes> {
    constructor(config?: IFlowGraphBitwiseBlockConfiguration) {
        super(
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            (a, b) => {
                if (typeof a === "boolean" && typeof b === "boolean") {
                    return a || b;
                } else if (typeof a === "number" && typeof b === "number") {
                    return a | b;
                } else if (typeof a === "object" && typeof b === "object") {
                    return new FlowGraphInteger(a.value | b.value);
                } else {
                    throw new Error(`Cannot perform bitwise OR on ${a} and ${b}`);
                }
            },
            FlowGraphBlockNames.BitwiseOr,
            config
        );
    }
}

/**
 * Bitwise XOR operation
 */
export class FlowGraphBitwiseXorBlock extends FlowGraphBinaryOperationBlock<FlowGraphBitwiseTypes, FlowGraphBitwiseTypes, FlowGraphBitwiseTypes> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            getRichTypeByFlowGraphType(config?.valueType || FlowGraphTypes.Integer),
            (a, b) => {
                if (typeof a === "boolean" && typeof b === "boolean") {
                    return a !== b;
                } else if (typeof a === "number" && typeof b === "number") {
                    return a ^ b;
                } else if (typeof a === "object" && typeof b === "object") {
                    return new FlowGraphInteger(a.value ^ b.value);
                } else {
                    throw new Error(`Cannot perform bitwise XOR on ${a} and ${b}`);
                }
            },
            FlowGraphBlockNames.BitwiseXor,
            config
        );
    }
}

/**
 * Bitwise left shift operation
 */
export class FlowGraphBitwiseLeftShiftBlock extends FlowGraphBinaryOperationBlock<FlowGraphInteger, FlowGraphInteger, FlowGraphInteger> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(
            RichTypeFlowGraphInteger,
            RichTypeFlowGraphInteger,
            RichTypeFlowGraphInteger,
            (a, b) => new FlowGraphInteger(a.value << b.value),
            FlowGraphBlockNames.BitwiseLeftShift,
            config
        );
    }
}

/**
 * Bitwise right shift operation
 */
export class FlowGraphBitwiseRightShiftBlock extends FlowGraphBinaryOperationBlock<FlowGraphInteger, FlowGraphInteger, FlowGraphInteger> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(
            RichTypeFlowGraphInteger,
            RichTypeFlowGraphInteger,
            RichTypeFlowGraphInteger,
            (a, b) => new FlowGraphInteger(a.value >> b.value),
            FlowGraphBlockNames.BitwiseRightShift,
            config
        );
    }
}

/**
 * Count leading zeros operation
 */
export class FlowGraphLeadingZerosBlock extends FlowGraphUnaryOperationBlock<FlowGraphInteger, FlowGraphInteger> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeFlowGraphInteger, RichTypeFlowGraphInteger, (a) => new FlowGraphInteger(Math.clz32(a.value)), FlowGraphBlockNames.LeadingZeros, config);
    }
}

/**
 * Count trailing zeros operation
 */
export class FlowGraphTrailingZerosBlock extends FlowGraphUnaryOperationBlock<FlowGraphInteger, FlowGraphInteger> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(
            RichTypeFlowGraphInteger,
            RichTypeFlowGraphInteger,
            (a) => new FlowGraphInteger(a.value ? 31 - Math.clz32(a.value & -a.value) : 32),
            FlowGraphBlockNames.TrailingZeros,
            config
        );
    }
}

/**
 * Given a number (which is converted to a 32-bit integer), return the
 * number of bits set to one on that number.
 * @param n the number to run the op on
 * @returns the number of bits set to one on that number
 */
function CountOnes(n: number) {
    let result = 0;
    while (n) {
        // This zeroes out all bits except for the least significant one.
        // So if the bit is set, it will be 1, otherwise it will be 0.
        result += n & 1;
        // Unsigned shifting is required so negative int32 values converge to zero.
        n >>>= 1;
    }
    return result;
}

/**
 * Count one bits operation
 */
export class FlowGraphOneBitsCounterBlock extends FlowGraphUnaryOperationBlock<FlowGraphInteger, FlowGraphInteger> {
    constructor(config?: IFlowGraphBlockConfiguration) {
        super(RichTypeFlowGraphInteger, RichTypeFlowGraphInteger, (a) => new FlowGraphInteger(CountOnes(a.value)), FlowGraphBlockNames.OneBitsCounter, config);
    }
}

/**
 * Converts a linear sRGB color to OkLCh (the polar form of the Oklab color space).
 * Uses Ottosson's original single-precision matrices.
 * The RGB inputs are treated as linear; hue is returned in radians.
 * @param r linear red component
 * @param g linear green component
 * @param b linear blue component
 * @returns the OkLCh lightness (l), chroma (c) and hue (h, radians)
 */
function RgbToOkLch(r: number, g: number, b: number): { l: number; c: number; h: number } {
    // Linear sRGB -> LMS cone responses.
    const long = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
    const medium = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
    const short = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
    // Non-linearity (cube root) then LMS' -> Oklab.
    const lRoot = Math.cbrt(long);
    const mRoot = Math.cbrt(medium);
    const sRoot = Math.cbrt(short);
    const okL = 0.2104542553 * lRoot + 0.793617785 * mRoot - 0.0040720468 * sRoot;
    const okA = 1.9779984951 * lRoot - 2.428592205 * mRoot + 0.4505937099 * sRoot;
    const okB = 0.0259040371 * lRoot + 0.7827717662 * mRoot - 0.808675766 * sRoot;
    // Oklab -> OkLCh (polar form).
    return { l: okL, c: Math.hypot(okA, okB), h: Math.atan2(okB, okA) };
}

/**
 * Converts an OkLCh color to linear sRGB. Inverse of {@link RgbToOkLch}; hue is in radians.
 * @param l OkLCh lightness
 * @param c OkLCh chroma
 * @param h OkLCh hue in radians
 * @returns the linear sRGB red (r), green (g) and blue (b) components
 */
function OkLchToRgb(l: number, c: number, h: number): { r: number; g: number; b: number } {
    // OkLCh -> Oklab.
    const okA = c * Math.cos(h);
    const okB = c * Math.sin(h);
    // Oklab -> LMS' then cube to LMS.
    const lPrime = l + 0.3963377774 * okA + 0.2158037573 * okB;
    const mPrime = l - 0.1055613458 * okA - 0.0638541728 * okB;
    const sPrime = l - 0.0894841775 * okA - 1.291485548 * okB;
    const long = lPrime * lPrime * lPrime;
    const medium = mPrime * mPrime * mPrime;
    const short = sPrime * sPrime * sPrime;
    // LMS -> linear sRGB.
    return {
        r: 4.0767416621 * long - 3.3077115913 * medium + 0.2309699292 * short,
        g: -1.2684380046 * long + 2.6097574011 * medium - 0.3413193965 * short,
        b: -0.0041960863 * long - 0.7034186147 * medium + 1.707614701 * short,
    };
}

/**
 * Block that converts a linear sRGB color (r, g, b) to OkLCh (l, c, h). Hue is in radians.
 */
export class FlowGraphRGBToOkLChBlock extends FlowGraphBlock {
    /**
     * Input connection: the linear red component.
     */
    public readonly r: FlowGraphDataConnection<number>;
    /**
     * Input connection: the linear green component.
     */
    public readonly g: FlowGraphDataConnection<number>;
    /**
     * Input connection: the linear blue component.
     */
    public readonly b: FlowGraphDataConnection<number>;
    /**
     * Output connection: the OkLCh lightness.
     */
    public readonly l: FlowGraphDataConnection<number>;
    /**
     * Output connection: the OkLCh chroma.
     */
    public readonly c: FlowGraphDataConnection<number>;
    /**
     * Output connection: the OkLCh hue, in radians.
     */
    public readonly h: FlowGraphDataConnection<number>;

    constructor(config?: IFlowGraphBlockConfiguration) {
        super(config);
        this.r = this.registerDataInput("r", RichTypeNumber, 0);
        this.g = this.registerDataInput("g", RichTypeNumber, 0);
        this.b = this.registerDataInput("b", RichTypeNumber, 0);
        this.l = this.registerDataOutput("l", RichTypeNumber, 0);
        this.c = this.registerDataOutput("c", RichTypeNumber, 0);
        this.h = this.registerDataOutput("h", RichTypeNumber, 0);
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const { l, c, h } = RgbToOkLch(this.r.getValue(context), this.g.getValue(context), this.b.getValue(context));
        this.l.setValue(l, context);
        this.c.setValue(c, context);
        this.h.setValue(h, context);
    }

    public override getClassName(): string {
        return FlowGraphBlockNames.RGBToOkLCh;
    }
}

/**
 * Block that converts an OkLCh color (l, c, h) to linear sRGB (r, g, b). Hue is in radians.
 */
export class FlowGraphRGBFromOkLChBlock extends FlowGraphBlock {
    /**
     * Input connection: the OkLCh lightness.
     */
    public readonly l: FlowGraphDataConnection<number>;
    /**
     * Input connection: the OkLCh chroma.
     */
    public readonly c: FlowGraphDataConnection<number>;
    /**
     * Input connection: the OkLCh hue, in radians.
     */
    public readonly h: FlowGraphDataConnection<number>;
    /**
     * Output connection: the linear red component.
     */
    public readonly r: FlowGraphDataConnection<number>;
    /**
     * Output connection: the linear green component.
     */
    public readonly g: FlowGraphDataConnection<number>;
    /**
     * Output connection: the linear blue component.
     */
    public readonly b: FlowGraphDataConnection<number>;

    constructor(config?: IFlowGraphBlockConfiguration) {
        super(config);
        this.l = this.registerDataInput("l", RichTypeNumber, 0);
        this.c = this.registerDataInput("c", RichTypeNumber, 0);
        this.h = this.registerDataInput("h", RichTypeNumber, 0);
        this.r = this.registerDataOutput("r", RichTypeNumber, 0);
        this.g = this.registerDataOutput("g", RichTypeNumber, 0);
        this.b = this.registerDataOutput("b", RichTypeNumber, 0);
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const { r, g, b } = OkLchToRgb(this.l.getValue(context), this.c.getValue(context), this.h.getValue(context));
        this.r.setValue(r, context);
        this.g.setValue(g, context);
        this.b.setValue(b, context);
    }

    public override getClassName(): string {
        return FlowGraphBlockNames.RGBFromOkLCh;
    }
}

let _Registered = false;
/**
 * Register side effects for flowGraphMathBlocks.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterFlowGraphMathBlocks(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    RegisterClass(FlowGraphBlockNames.Add, FlowGraphAddBlock);
    RegisterClass(FlowGraphBlockNames.Subtract, FlowGraphSubtractBlock);
    RegisterClass(FlowGraphBlockNames.Multiply, FlowGraphMultiplyBlock);
    RegisterClass(FlowGraphBlockNames.Divide, FlowGraphDivideBlock);
    RegisterClass(FlowGraphBlockNames.Random, FlowGraphRandomBlock);
    RegisterClass(FlowGraphBlockNames.E, FlowGraphEBlock);
    RegisterClass(FlowGraphBlockNames.PI, FlowGraphPiBlock);
    RegisterClass(FlowGraphBlockNames.Tau, FlowGraphTauBlock);
    RegisterClass(FlowGraphBlockNames.Inf, FlowGraphInfBlock);
    RegisterClass(FlowGraphBlockNames.NaN, FlowGraphNaNBlock);
    RegisterClass(FlowGraphBlockNames.Abs, FlowGraphAbsBlock);
    RegisterClass(FlowGraphBlockNames.Sign, FlowGraphSignBlock);
    RegisterClass(FlowGraphBlockNames.Trunc, FlowGraphTruncBlock);
    RegisterClass(FlowGraphBlockNames.Floor, FlowGraphFloorBlock);
    RegisterClass(FlowGraphBlockNames.Ceil, FlowGraphCeilBlock);
    RegisterClass(FlowGraphBlockNames.Round, FlowGraphRoundBlock);
    RegisterClass(FlowGraphBlockNames.Fraction, FlowGraphFractionBlock);
    RegisterClass(FlowGraphBlockNames.Negation, FlowGraphNegationBlock);
    RegisterClass(FlowGraphBlockNames.Modulo, FlowGraphModuloBlock);
    RegisterClass(FlowGraphBlockNames.Min, FlowGraphMinBlock);
    RegisterClass(FlowGraphBlockNames.Max, FlowGraphMaxBlock);
    RegisterClass(FlowGraphBlockNames.Clamp, FlowGraphClampBlock);
    RegisterClass(FlowGraphBlockNames.Saturate, FlowGraphSaturateBlock);
    RegisterClass(FlowGraphBlockNames.MathInterpolation, FlowGraphMathInterpolationBlock);
    RegisterClass(FlowGraphBlockNames.MathSlerp, FlowGraphMathSlerpBlock);
    RegisterClass(FlowGraphBlockNames.SmoothStep, FlowGraphMathSmoothStepBlock);
    RegisterClass(FlowGraphBlockNames.RGBToOkLCh, FlowGraphRGBToOkLChBlock);
    RegisterClass(FlowGraphBlockNames.RGBFromOkLCh, FlowGraphRGBFromOkLChBlock);
    RegisterClass(FlowGraphBlockNames.Equality, FlowGraphEqualityBlock);
    RegisterClass(FlowGraphBlockNames.LessThan, FlowGraphLessThanBlock);
    RegisterClass(FlowGraphBlockNames.LessThanOrEqual, FlowGraphLessThanOrEqualBlock);
    RegisterClass(FlowGraphBlockNames.GreaterThan, FlowGraphGreaterThanBlock);
    RegisterClass(FlowGraphBlockNames.GreaterThanOrEqual, FlowGraphGreaterThanOrEqualBlock);
    RegisterClass(FlowGraphBlockNames.IsNaN, FlowGraphIsNanBlock);
    RegisterClass(FlowGraphBlockNames.IsInfinity, FlowGraphIsInfinityBlock);
    RegisterClass(FlowGraphBlockNames.DegToRad, FlowGraphDegToRadBlock);
    RegisterClass(FlowGraphBlockNames.RadToDeg, FlowGraphRadToDegBlock);
    RegisterClass(FlowGraphBlockNames.Asin, FlowGraphAsinBlock);
    RegisterClass(FlowGraphBlockNames.Acos, FlowGraphAcosBlock);
    RegisterClass(FlowGraphBlockNames.Atan, FlowGraphAtanBlock);
    RegisterClass(FlowGraphBlockNames.Atan2, FlowGraphAtan2Block);
    RegisterClass(FlowGraphBlockNames.Sinh, FlowGraphSinhBlock);
    RegisterClass(FlowGraphBlockNames.Cosh, FlowGraphCoshBlock);
    RegisterClass(FlowGraphBlockNames.Tanh, FlowGraphTanhBlock);
    RegisterClass(FlowGraphBlockNames.Asinh, FlowGraphAsinhBlock);
    RegisterClass(FlowGraphBlockNames.Acosh, FlowGraphAcoshBlock);
    RegisterClass(FlowGraphBlockNames.Atanh, FlowGraphAtanhBlock);
    RegisterClass(FlowGraphBlockNames.Exponential, FlowGraphExpBlock);
    RegisterClass(FlowGraphBlockNames.Log, FlowGraphLogBlock);
    RegisterClass(FlowGraphBlockNames.Log2, FlowGraphLog2Block);
    RegisterClass(FlowGraphBlockNames.Log10, FlowGraphLog10Block);
    RegisterClass(FlowGraphBlockNames.SquareRoot, FlowGraphSquareRootBlock);
    RegisterClass(FlowGraphBlockNames.CubeRoot, FlowGraphCubeRootBlock);
    RegisterClass(FlowGraphBlockNames.Power, FlowGraphPowerBlock);
    RegisterClass(FlowGraphBlockNames.BitwiseNot, FlowGraphBitwiseNotBlock);
    RegisterClass(FlowGraphBlockNames.BitwiseAnd, FlowGraphBitwiseAndBlock);
    RegisterClass(FlowGraphBlockNames.BitwiseOr, FlowGraphBitwiseOrBlock);
    RegisterClass(FlowGraphBlockNames.BitwiseXor, FlowGraphBitwiseXorBlock);
    RegisterClass(FlowGraphBlockNames.BitwiseLeftShift, FlowGraphBitwiseLeftShiftBlock);
    RegisterClass(FlowGraphBlockNames.BitwiseRightShift, FlowGraphBitwiseRightShiftBlock);
    RegisterClass(FlowGraphBlockNames.LeadingZeros, FlowGraphLeadingZerosBlock);
    RegisterClass(FlowGraphBlockNames.TrailingZeros, FlowGraphTrailingZerosBlock);
    RegisterClass(FlowGraphBlockNames.OneBitsCounter, FlowGraphOneBitsCounterBlock);
}
